import axiosInstance from '@/api/axiosInstance';
import { Group } from '@/components/_common';
import ActionIcon from '@/components/_common/ActionIcon';
import { useDisclosure } from '@/hooks';
import {
  deleteRequestFromQueue,
  getAllRequestQueue,
} from '@/utils/tefRequestsIndexedDB';
import { Button, Modal, Popover, Spin, Table } from 'antd';
import { Method } from 'axios';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Info, RefreshCcw } from 'react-feather';
import StatusTag from '../_common/StatusTag';

interface RequestData {
  id: number;
  description: string;
  createdAt: string;
  url: string;
  baseUrl: string;
  data: string;
  header: Record<string, string>;
  method: Method;
}

export default function RequestQueueStatus() {
  const [requests, setRequests] = useState<RequestData[]>([]);
  const [refreshing, setRefreshing] = useState<number>();
  const [opened, action] = useDisclosure(false, {
    onClose: () => {
      setRefreshing(undefined);
    },
  });
  const [isOnline, setIsOnline] = useState(false);

  const [tooltipOpened, tooltipAction] = useDisclosure();

  const getRequestQueueOnIDB = async () => {
    const requestQueue = await getAllRequestQueue();

    setRequests(
      requestQueue.map((item) => ({ ...JSON.parse(item.data), id: item.id })),
    );

    if (opened && !requestQueue.length) {
      action.close();
    }
  };

  useEffect(() => {
    getRequestQueueOnIDB();

    const interval = setInterval(() => {
      if (navigator.onLine) {
        setIsOnline(true);
        getRequestQueueOnIDB();
      } else {
        setIsOnline(false);
      }
    }, 15000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (requests.length > 0) {
      tooltipAction.open();
      timeout = setTimeout(tooltipAction.close, 5000);
    }

    return () => clearTimeout(timeout);
  }, [requests.length]);

  useEffect(() => {
    if (!opened || !('serviceWorker' in navigator)) return;

    const handler = (ev: MessageEvent) => {
      switch (ev.data.type) {
        case 'TEF_RETRY_INIT':
          setRefreshing(ev.data.payload.id);
          break;
        case 'TEF_RETRY_END':
          setRefreshing(undefined);
          break;
      }
    };

    const watchRequestLoad = async () => {
      const worker = await navigator.serviceWorker.ready;

      if (worker.active) {
        navigator.serviceWorker?.addEventListener('message', handler);
      }
    };

    watchRequestLoad();

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handler);
    };
  }, [opened]);

  const refresh = async ({ id, ...data }: RequestData) => {
    try {
      setRefreshing(id);
      await axiosInstance({ ...data, timeout: 15000 });
      await deleteRequestFromQueue(id);
    } finally {
      setRefreshing(undefined);
    }
  };

  if (!requests.length) return null;

  return (
    <>
      <div style={{ position: 'fixed', zIndex: 600, left: 24, bottom: 24 }}>
        <Popover
          popupVisible={tooltipOpened}
          content={
            tooltipOpened
              ? `Há ${requests.length} ${
                  requests.length > 1 ? 'requisições' : 'requisição'
                }  na fila`
              : null
          }
          open={tooltipOpened}
          placement="right"
          zIndex={50}
        >
          <Button
            shape="circle"
            icon={<Info color="orange" size={18} />}
            size="large"
            onClick={action.open}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 50,
              height: 50,
              boxShadow: '10px 10px 50px rgba(0,0,0,0.15)',
            }}
          />
        </Popover>
      </div>

      <Modal
        title="Falhas de envio"
        onCancel={action.close}
        open={opened}
        width={700}
      >
        {isOnline ? (
          <StatusTag label="Com internet" color="green" />
        ) : (
          <StatusTag label="Sem internet" color="red" />
        )}

        <Table
          style={{ marginTop: 24 }}
          dataSource={requests}
          columns={[
            {
              title: 'Descrição',
              width: 300,
              ellipsis: true,
              dataIndex: 'description',
            },
            {
              title: 'URL',
              dataIndex: 'url',
              ellipsis: true,
              width: 200,
            },
            {
              title: 'Solicitação',
              render: (_, item) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                  {moment(item.createdAt).format('DD/MM/YYYY HH:mm:ss')}
                </span>
              ),
              width: 200,
            },
            {
              title: 'Reenviar',
              align: 'center',
              fixed: 'right',
              render: (_, item) => (
                <Group position="center">
                  <ActionIcon
                    onClick={() => refresh(item)}
                    disabled={refreshing === item.id}
                  >
                    {refreshing === item.id ? (
                      <Spin size="small" />
                    ) : (
                      <RefreshCcw size={20} />
                    )}
                  </ActionIcon>
                </Group>
              ),
            },
          ]}
          pagination={false}
          scroll={{ x: true }}
        />
      </Modal>
    </>
  );
}
